import type { ReactNode } from 'react'

import { cn } from '../../../components/cn'
import { PJ } from '../../../copy/pjshell-text'
import type { PjshellId } from '../../../copy/pjshell'

export function ModulePage({
  testid,
  children,
  layout = 'scroll',
}: {
  testid: string
  children: ReactNode
  /**
   * `scroll` — module body scrolls as a page (default).
   * `fill` — pin to the shell height so an embedded chat can keep its
   * composer visible (same contract as LuluFrame: clip the bottom = bug).
   */
  layout?: 'scroll' | 'fill'
}) {
  return (
    <div
      data-testid={testid}
      className={cn(
        'projects-theme min-h-0 min-w-0 flex-1 bg-npc-lulu-bg-0 px-npc-page-x pt-npc-grid-sm text-npc-lulu-t1',
        layout === 'fill'
          ? 'relative flex flex-col overflow-hidden pb-3'
          : 'overflow-y-auto pb-10',
      )}
    >
      {children}
    </div>
  )
}

export function ModuleHeader({
  title,
  subtitle,
  actions,
}: {
  title: ReactNode
  subtitle?: ReactNode
  actions?: ReactNode
}) {
  return (
    <header className="-mx-npc-page-x mb-7 flex items-start justify-between gap-npc-grid border-b border-npc-lulu-line-1 px-npc-page-x pb-npc-gap-sm pt-0 max-[600px]:flex-col max-[600px]:items-stretch">
      <div className="min-w-0">
        <h1 className="m-0 font-npc-display text-npc-modal-title font-semibold tracking-[-0.035em] text-npc-lulu-t1">
          {title}
        </h1>
        {subtitle ? (
          <p className="mb-0 mt-npc-gap-xs text-npc-body-sm font-medium leading-[1.65] text-npc-lulu-t3">{subtitle}</p>
        ) : null}
      </div>
      {actions ? <div className="flex shrink-0 flex-wrap items-center gap-npc-gap-sm">{actions}</div> : null}
    </header>
  )
}

export function GroupLabel({ children }: { children: ReactNode }) {
  return (
    <div className="mb-npc-gap-sm mt-npc-gap-sm flex items-center gap-npc-gap-sm first:mt-0">
      <span className="pj-caption shrink-0 font-npc-display text-npc-meta tracking-[0.02em]">
        {children}
      </span>
      <span aria-hidden="true" className="h-px min-w-0 flex-1 bg-npc-lulu-line-1" />
    </div>
  )
}

export function EmptyState({
  title,
  body,
  action,
  testid,
}: {
  title: ReactNode
  body?: ReactNode
  action?: ReactNode
  testid?: string
}) {
  return (
    <div
      data-testid={testid}
      className="pj-card pj-empty-ish border border-dashed border-npc-lulu-line-3 px-npc-grid-sm py-npc-gap-lg"
    >
      <div className="text-npc-body-sm font-semibold text-npc-lulu-t1">{title}</div>
      {body ? <div className="pj-caption mt-npc-gap-xs text-npc-caption leading-[1.65]">{body}</div> : null}
      {action ? <div className="mt-npc-gap-sm">{action}</div> : null}
    </div>
  )
}

export function StatusPill({
  tone = 'neutral',
  children,
}: {
  tone?: 'ok' | 'warn' | 'bad' | 'neutral' | 'need' | 'muted'
  children: ReactNode
}) {
  // Soft Structuralism status map: green only 已上线 (ok); coral soft 需处理 (need); grey 进行中 (neutral).
  if (tone === 'need' || tone === 'ok' || tone === 'muted' || tone === 'neutral') {
    const cls =
      tone === 'need'
        ? 'pj-status pj-status-need'
        : tone === 'ok'
          ? 'pj-status pj-status-live'
          : tone === 'muted'
            ? 'pj-status pj-status-muted'
            : 'pj-status pj-status-run'
    return (
      <span className={cls}>
        <span aria-hidden="true" className="pj-status-dot" />
        {children}
      </span>
    )
  }
  const dot =
    tone === 'warn'
      ? 'bg-npc-lulu-warn'
      : tone === 'bad'
        ? 'bg-npc-lulu-bad'
        : 'bg-npc-lulu-t4'
  return (
    <span className="inline-flex items-center gap-1 rounded-npc-chip-inline bg-npc-lulu-bg-3 px-[7px] py-0.5 text-npc-meta text-npc-lulu-t2">
      <span aria-hidden="true" className={cn('size-1.5 shrink-0 rounded-npc-full', dot)} />
      {children}
    </span>
  )
}

export function ModuleSkeleton() {
  return (
    <div aria-busy="true" className="flex flex-col gap-npc-gap-sm">
      <div className="h-3 w-1/3 animate-npc-pulse rounded-npc-chip bg-npc-lulu-bg-3" />
      <div className="h-3 w-2/3 animate-npc-pulse rounded-npc-chip bg-npc-lulu-bg-3" />
      <div className="h-3 w-1/2 animate-npc-pulse rounded-npc-chip bg-npc-lulu-bg-3" />
    </div>
  )
}

export function ModulePanel({
  title,
  dashed = false,
  emptyish = false,
  testid,
  badge,
  children,
}: {
  title: ReactNode
  dashed?: boolean
  emptyish?: boolean
  testid?: string
  badge?: ReactNode
  children: ReactNode
}) {
  return (
    <section
      data-testid={testid}
      className={cn(
        'pj-card border',
        dashed ? 'border-dashed border-npc-lulu-line-3' : 'border-solid',
        emptyish && 'pj-empty-ish',
      )}
    >
      <div className="flex items-center justify-between gap-npc-gap-sm border-b border-npc-lulu-line-1 px-npc-grid-sm py-npc-gap-sm">
        <div className="min-w-0 text-npc-body-sm font-semibold text-npc-lulu-t1">{title}</div>
        {badge ? <div className="shrink-0 text-npc-caption">{badge}</div> : null}
      </div>
      <div className="px-npc-grid-sm py-npc-gap-sm text-npc-caption leading-[1.6] text-npc-lulu-t2">{children}</div>
    </section>
  )
}

export function KvRows({
  rows,
  wide = false,
}: {
  rows: Array<{ key: ReactNode; value: ReactNode; testid?: string }>
  wide?: boolean
}) {
  return (
    <div className="flex flex-col gap-npc-gap-xs">
      {rows.map((row, index) => (
        <div key={index} className="flex gap-npc-gap-sm" data-testid={row.testid}>
          <div
            className={cn(
              'shrink-0 text-npc-caption-sm text-npc-lulu-t3',
              wide ? 'w-[96px]' : 'w-[72px]',
            )}
          >
            {row.key}
          </div>
          <div className="min-w-0 flex-1 text-npc-caption text-npc-lulu-t1">{row.value}</div>
        </div>
      ))}
    </div>
  )
}

export function ModuleNotice({
  tone,
  title,
  body,
  stats,
  action,
  testid,
}: {
  tone: 'neutral' | 'ok' | 'warn' | 'danger'
  title: ReactNode
  body?: ReactNode
  stats?: Array<{ value: ReactNode; label: ReactNode }>
  action?: ReactNode
  testid?: string
}) {
  const border =
    tone === 'ok'
      ? 'border-npc-lulu-ok/35 bg-npc-lulu-ok/15'
      : tone === 'warn'
        ? 'border-npc-lulu-warn/35 bg-npc-lulu-warn/15'
        : tone === 'danger'
          ? 'border-npc-lulu-bad/40 bg-npc-lulu-bad/15'
          : 'border-npc-lulu-line-2 bg-npc-lulu-bg-1'
  const titleColor =
    tone === 'ok'
      ? 'text-npc-lulu-ok'
      : tone === 'warn'
        ? 'text-npc-lulu-warn'
        : tone === 'danger'
          ? 'text-npc-lulu-bad'
          : 'text-npc-lulu-t1'
  const dot =
    tone === 'ok'
      ? 'bg-npc-lulu-ok'
      : tone === 'warn'
        ? 'bg-npc-lulu-warn'
        : tone === 'danger'
          ? 'bg-npc-lulu-bad'
          : 'bg-npc-lulu-t4'
  return (
    <div
      data-testid={testid}
      className={cn('mb-npc-grid flex items-start gap-npc-gap-sm rounded-npc-chip border bg-npc-lulu-bg-1 px-[15px] py-[13px]', border)}
    >
      <span aria-hidden="true" className={cn('mt-1.5 size-[7px] shrink-0 rounded-npc-full', dot)} />
      <div className="min-w-0 flex-1">
        <div className={cn('text-npc-body-sm font-medium', titleColor)}>{title}</div>
        {body ? <div className="mt-npc-gap-xs text-npc-caption-sm leading-[1.65] text-npc-lulu-t3">{body}</div> : null}
        {stats && stats.length > 0 ? (
          <div className="mt-npc-gap-sm flex flex-wrap gap-5">
            {stats.map((stat, index) => (
              <div key={index}>
                <div className="font-npc-mono text-npc-stat-sm font-medium leading-none text-npc-lulu-t1">
                  {stat.value}
                </div>
                <div className="mt-npc-gap-xs text-npc-caption-sm text-npc-lulu-t3">{stat.label}</div>
              </div>
            ))}
          </div>
        ) : null}
        {action ? <div className="mt-npc-gap-sm">{action}</div> : null}
      </div>
    </div>
  )
}

export function FilterBar({
  search,
  searchPlaceholder,
  searchLabel,
  filters,
}: {
  search?: {
    value: string
    onChange: (value: string) => void
  }
  searchPlaceholder?: string
  searchLabel?: string
  filters: Array<{ id: string; label: ReactNode; count?: number; selected: boolean; onSelect: () => void }>
}) {
  return (
    <div className="mb-npc-grid flex flex-wrap items-center gap-npc-gap-sm">
      {search ? (
        <label className="pj-search flex min-w-0 flex-1 basis-[180px] items-center gap-npc-gap-xs px-3.5">
          <span className="sr-only">{searchLabel ?? searchPlaceholder}</span>
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="shrink-0 text-npc-lulu-t4"
            aria-hidden="true"
          >
            <circle cx="11" cy="11" r="8" />
            <path d="M21 21l-4.35-4.35" />
          </svg>
          <input
            type="search"
            value={search.value}
            onChange={(event) => search.onChange(event.target.value)}
            placeholder={searchPlaceholder}
            className="h-full min-w-0 flex-1 border-0 bg-transparent text-npc-body-sm text-npc-lulu-t1 outline-none placeholder:text-npc-lulu-t4"
          />
        </label>
      ) : null}
      <div className="flex flex-wrap gap-npc-gap-xs">
        {filters.map((filter) => (
          <button
            key={filter.id}
            type="button"
            aria-pressed={filter.selected}
            className={cn(
              'pj-filter-chip inline-flex items-center gap-1.5',
              filter.selected && 'pj-filter-active',
            )}
            onClick={filter.onSelect}
          >
            <span>{filter.label}</span>
            {filter.count != null ? (
              <span className="font-npc-mono text-npc-meta-sm text-npc-lulu-t4">{filter.count}</span>
            ) : null}
          </button>
        ))}
      </div>
    </div>
  )
}

export function DataTable({
  testid,
  columns,
  grid,
  children,
  hint,
}: {
  testid?: string
  columns: ReactNode[]
  grid: string
  children: ReactNode
  hint?: ReactNode
}) {
  return (
    <div>
      {hint ? <p className="mb-npc-gap-sm mt-0 text-npc-meta text-npc-lulu-t4">{hint}</p> : null}
      <div data-testid={testid} className="pj-soft-table max-w-full min-w-0 overflow-x-auto">
        <div className="pj-soft-table-head grid font-npc-display tracking-npc-label" style={{ gridTemplateColumns: grid }}>
          {columns.map((column, index) => (
            <div key={index} className="px-[18px] py-2.5">
              {column}
            </div>
          ))}
        </div>
        {children}
      </div>
    </div>
  )
}

export function dataRowClass(warn?: boolean) {
  return cn(
    'pj-soft-row grid text-npc-body-sm text-npc-lulu-t1',
    warn && 'pj-need-row',
  )
}

export function DataRow({
  grid,
  warn,
  children,
  testid,
}: {
  grid: string
  warn?: boolean
  children: ReactNode
  testid?: string
}) {
  return (
    <div data-testid={testid} className={dataRowClass(warn)} style={{ gridTemplateColumns: grid }}>
      {children}
    </div>
  )
}

export function DataCell({ children, mono = false }: { children: ReactNode; mono?: boolean }) {
  return (
    <div className={cn('flex min-w-0 items-center px-[18px] py-[14px]', mono && 'font-npc-mono text-npc-caption')}>
      {children}
    </div>
  )
}

export type StepState = 'done' | 'now' | 'fail' | 'wait'

export function StepTimeline({
  steps,
  testid,
}: {
  steps: Array<{ title: PjshellId; state: StepState; note?: PjshellId; meta?: ReactNode }>
  testid?: string
}) {
  return (
    <ol data-testid={testid} className="m-0 flex list-none flex-col gap-0 p-0">
      {steps.map((step, index) => {
        const last = index === steps.length - 1
        const dot =
          step.state === 'done'
            ? 'bg-npc-lulu-ok'
            : step.state === 'now'
              ? 'border-2 border-npc-lulu-warn bg-transparent'
              : step.state === 'fail'
                ? 'bg-npc-lulu-bad'
                : 'border border-npc-lulu-line-3 bg-transparent'
        const line = step.state === 'done' ? 'bg-npc-lulu-ok' : 'bg-npc-lulu-line-2'
        const weight = step.state === 'done' || step.state === 'now' ? 'font-medium text-npc-lulu-t1' : 'text-npc-lulu-t3'
        return (
          <li key={step.title} className="flex gap-npc-gap-sm">
            <div className="flex w-3 shrink-0 flex-col items-center pt-0.5">
              <span
                aria-hidden="true"
                className={cn(
                  'size-[9px] rounded-npc-full',
                  dot,
                  step.state === 'now' && 'animate-spin border-t-transparent',
                )}
              />
              {last ? null : <span aria-hidden="true" className={cn('mt-0.5 min-h-4 w-px flex-1', line)} />}
            </div>
            <div className={cn('min-w-0 pb-npc-gap-sm', last && 'pb-0')}>
              <div className={cn('text-npc-body-sm', weight)}>
                <PJ id={step.title} />
              </div>
              {step.note ? (
                <div className="mt-0.5 text-npc-meta text-npc-lulu-t4">
                  <PJ id={step.note} />
                </div>
              ) : null}
              {step.meta ? (
                <div className="mt-0.5 text-npc-meta leading-[1.45] text-npc-lulu-t4">
                  {step.meta}
                </div>
              ) : null}
            </div>
          </li>
        )
      })}
    </ol>
  )
}

export function Unverified({ testid }: { testid?: string }) {
  return (
    <span data-testid={testid} className="text-npc-lulu-t3">
      <PJ id="pjshell.unverified" />
    </span>
  )
}

export function Uncollected({ testid }: { testid?: string }) {
  return (
    <span data-testid={testid} className="text-npc-lulu-t3">
      <PJ id="pjshell.fact.uncollected" />
    </span>
  )
}

export function Unconfigured({ testid }: { testid?: string }) {
  return (
    <span data-testid={testid} className="text-npc-lulu-t3">
      <PJ id="pjshell.fact.unconfigured" />
    </span>
  )
}

export function ModuleError({ message }: { message: string }) {
  return (
    <div className="rounded-npc-chip border border-npc-lulu-bad/40 bg-npc-lulu-bg-1 px-npc-grid-sm py-npc-gap-sm text-npc-caption text-npc-lulu-bad">
      {message}
    </div>
  )
}

export function moduleTitle(name: string, tab: string): string {
  return `${name} · ${tab}`
}

export const luluButtonClass =
  'pj-btn-ghost inline-flex h-[34px] items-center justify-center rounded-full px-3.5 text-npc-caption font-medium transition-[background] duration-npc-hover disabled:cursor-not-allowed disabled:opacity-50'

export const luluPrimaryButtonClass =
  'pj-btn-primary inline-flex h-[34px] items-center justify-center rounded-full px-3.5 text-npc-caption font-semibold transition-[filter] duration-npc-hover disabled:cursor-not-allowed disabled:opacity-50'

export const luluDangerButtonClass =
  'inline-flex h-[34px] min-h-11 items-center justify-center rounded-full bg-npc-lulu-bad px-3.5 text-npc-caption text-[var(--pj-accent-ink)] transition-[background] duration-npc-hover disabled:cursor-not-allowed disabled:opacity-50'
